import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Magnetic } from '@/components/fx/Magnetic';
import { getGsap } from '@/lib/motion';
import { canAnimate } from './heroEnv';
import './hero.css';

// HeroScrollCue — tells a first-time visitor there is more below the stage
// (immersive redesign, 2026-09-24). A light bead runs down a hairline next to
// the label; tapping it glides to the tool cards. It fades out as soon as the
// page starts to move, because by then it has done its job.
//
// Short phones (hero.css): under 760px of height it is the bead alone, and
// under 700px it is gone, because the peeking tool cards are the cue there.
export function HeroScrollCue({ onGo }: { onGo: () => void }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !canAnimate()) return undefined;
    const { gsap } = getGsap();
    // autoAlpha, not opacity: once faded it is visibility:hidden, so the
    // invisible button leaves the tab order and stops catching taps.
    const tween = gsap.fromTo(el, { autoAlpha: 1 }, {
      autoAlpha: 0,
      ease: 'none',
      scrollTrigger: { trigger: document.documentElement, start: 'top top', end: '+=220', scrub: true },
    });
    return () => {
      tween.scrollTrigger?.kill();
      tween.kill();
      gsap.set(el, { clearProps: 'opacity,visibility' });
    };
  }, []);

  return (
    <div className="hero-cue relative z-10 mt-2 flex justify-center">
      <Magnetic strength={0.3} radius={48}>
        <button
          ref={ref}
          type="button"
          onClick={onGo}
          data-testid="hero-scroll-cue"
          aria-label={t('immersiveHero.scrollCue', { defaultValue: 'Scroll to explore' })}
          className="hero-cue__btn group flex min-h-[44px] min-w-[44px] items-center justify-center gap-3 rounded-full px-4 text-[0.8rem] font-semibold uppercase tracking-[0.22em] text-white/55 transition-colors duration-300 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 sm:text-[0.72rem]"
        >
          <span className="hero-cue-track" aria-hidden="true" />
          <span className="hero-cue__label" aria-hidden="true">{t('immersiveHero.scrollCue', { defaultValue: 'Scroll to explore' })}</span>
        </button>
      </Magnetic>
    </div>
  );
}
